import { HeurekaElementFactory } from "../util/HeurekaElementFactory";
import { Filter } from "./Filter";
import { element, move, moveAllChildren } from "../util/Utils";
import { filterHeaderButtons } from "../filtersheet/FilterSheetHeaderButtons";
import { eventQBus } from "../types/EventQBus";
import type { TrackingLabels } from "../tracking/TrackingLabels";
import { listFeatures, readLegacyLabels, submitActionTrackingFrom } from "../tracking/FeatureTracking";
import { updateSanPopularFacetValues } from "./SveltePopularFacetValues";
import { mergeLabel } from "../tracking/GlobalTracking";
import { OcFilterAccordion } from "./OcFilterAccordion";
import { createEventMergeId, submitEventTracking } from "../tracking/TrackingUtils";

const FILTER_TITLE_CLASS = "heureka_filterTitle";
const FILTER_TITLE_MODE_MODIFIER_PREFIX = `${FILTER_TITLE_CLASS}--`;
const FILTER_TITLE_MODE_ONLY_TITLE_MODIFIER = `${FILTER_TITLE_MODE_MODIFIER_PREFIX}onlyTitle`;
const FILTER_TITLE_MODE_FULL_MODIFIER = `${FILTER_TITLE_MODE_MODIFIER_PREFIX}full`;
const FILTER_TITLE_MODE_ONLY_POPULAR_VALUES_AND_TITLE_MODIFIER = `${FILTER_TITLE_MODE_MODIFIER_PREFIX}onlyPopularValuesAndTitle`;
const FILTER_TITLE_MODE_VALUES_AND_TITLE_MODIFIER = `${FILTER_TITLE_MODE_MODIFIER_PREFIX}valuesAndTitle`;
const FILTER_TITLE_INVALID_MODIFIER = `${FILTER_TITLE_MODE_MODIFIER_PREFIX}invalid`;

const VALUES_CLASS = `${FILTER_TITLE_CLASS}__values`;
const VALUES_SELECTOR = `.${VALUES_CLASS}`;
const MORE_CLASS = `${FILTER_TITLE_CLASS}__more`;
const FILTER_HEADER_BUTTONS_CLASS = "heureka_filterHeaderButtons";
const MORE_BUTTON_SELECTOR_DEPRECATED = `.${MORE_CLASS} button`;
const TOP_MORE_BUTTON_SELECTOR_DEPRECATED = `.${FILTER_HEADER_BUTTONS_CLASS} button`;
const TOP_MORE_ICON_SELECTOR_DEPRECATED = `${TOP_MORE_BUTTON_SELECTOR_DEPRECATED} .heureka_filterTitle__more--icon`;
const TOP_MORE_LABEL_SELECTOR_DEPRECATED = `${TOP_MORE_BUTTON_SELECTOR_DEPRECATED} .heureka_filterTitle__more--label`;
const MORE_BUTTON_SELECTOR = `.${MORE_CLASS} oc-link-v2`;
const TOP_MORE_BUTTON_SELECTOR = `.${FILTER_HEADER_BUTTONS_CLASS} oc-link-v2`;
const TOP_MORE_ICON_SELECTOR = `${TOP_MORE_BUTTON_SELECTOR} .heureka_filterTitle__more--icon`;
const TOP_MORE_LABEL_SELECTOR = `${TOP_MORE_BUTTON_SELECTOR} .heureka_filterTitle__more--label`;

export type FilterTitleMode = "onlyTitle" | "full" | "onlyPopularValuesAndTitle" | "valuesAndTitle";

export class FilterTitle {
  static readonly factory = HeurekaElementFactory.byClass(FILTER_TITLE_CLASS, FilterTitle);

  /*               */
  constructor(readonly elem: HTMLElement) {}

  /*                  */

  static filterId(id: string, root?: ParentNode | null) {
    return FilterTitle.factory.pick(`.${FILTER_TITLE_CLASS}[data-filter-id="${id}"]`, root);
  }

  static footer(root?: ParentNode | null) {
    return FilterTitle.factory.pick(`.${FILTER_TITLE_CLASS}.${FILTER_TITLE_CLASS}--footer`, root);
  }

  /*                       */

  get filterId(): string {
    return this.elem.dataset.filterId || "";
  }

  get hidden(): boolean {
    return this.elem.hidden;
  }

  get filterTitle() {
    return this.elem.dataset.filterTitle;
  }

  get accordion(): OcFilterAccordion {
    return OcFilterAccordion.filterId(this.filterId);
  }

  get referencedFilter() {
    return Filter.filterId(this.filterId);
  }

  get filter() {
    return Filter.factory.pick(undefined, this.elem);
  }

  set filter(filter) {
    if (filter) {
      move(filter.form, this.values);
    }
  }

  get filterHeaderButtons() {
    return filterHeaderButtons(this.elem);
  }

  set filterHeaderButtons(filterHeaderButtons) {
    if (filterHeaderButtons) {
      moveAllChildren(filterHeaderButtons, this.filterHeaderButtons);
    }
  }

  get values() {
    return element(VALUES_SELECTOR, this.elem);
  }

  get moreButtonDeprecated() {
    return element(MORE_BUTTON_SELECTOR_DEPRECATED, this.elem);
  }

  get topMoreButtonDeprecated() {
    return element(TOP_MORE_BUTTON_SELECTOR_DEPRECATED, this.elem);
  }

  get topMoreIconDeprecated() {
    return element(TOP_MORE_ICON_SELECTOR_DEPRECATED, this.elem);
  }

  get topMoreLabelDeprecated() {
    return element(TOP_MORE_LABEL_SELECTOR_DEPRECATED, this.elem);
  }

  get moreButton() {
    return element(MORE_BUTTON_SELECTOR, this.elem);
  }

  get topMoreButton() {
    return element(TOP_MORE_BUTTON_SELECTOR, this.elem);
  }

  get topMoreIcon() {
    return element(TOP_MORE_ICON_SELECTOR, this.elem);
  }

  get topMoreLabel() {
    return element(TOP_MORE_LABEL_SELECTOR, this.elem);
  }

  get onlyTitleMode(): boolean {
    return this.elem.classList.contains(FILTER_TITLE_MODE_ONLY_TITLE_MODIFIER);
  }

  get fullMode(): boolean {
    return this.elem.classList.contains(FILTER_TITLE_MODE_FULL_MODIFIER);
  }

  get valuesAndTitle(): boolean {
    return this.elem.classList.contains(FILTER_TITLE_MODE_VALUES_AND_TITLE_MODIFIER);
  }

  get onlyPopularValuesAndTitleMode(): boolean {
    return this.elem.classList.contains(FILTER_TITLE_MODE_ONLY_POPULAR_VALUES_AND_TITLE_MODIFIER);
  }

  set onlyPopularValuesAndTitleMode(state: boolean) {
    if (state && this.onlyTitleMode) {
      this.mode = "onlyPopularValuesAndTitle";
    }
    if (!state && this.onlyPopularValuesAndTitleMode) {
      this.mode = "onlyTitle";
    }
  }

  set mode(mode: FilterTitleMode) {
    this.elem.classList.remove(
      FILTER_TITLE_MODE_ONLY_TITLE_MODIFIER,
      FILTER_TITLE_MODE_FULL_MODIFIER,
      FILTER_TITLE_MODE_ONLY_POPULAR_VALUES_AND_TITLE_MODIFIER,
      FILTER_TITLE_MODE_VALUES_AND_TITLE_MODIFIER,
    );
    this.elem.classList.add(`${FILTER_TITLE_MODE_MODIFIER_PREFIX}${mode}`);
  }

  set sanPopularFacetValues(value: TrackingLabels["san_PopularFacetValues"]) {
    updateSanPopularFacetValues(this.elem, value);
  }

  mergeLabel<T extends keyof TrackingLabels>(label: T, value: TrackingLabels[T]) {
    mergeLabel(this.elem, label, value);
  }

  attachFilter() {
    const { accordion } = this;
    if (accordion) {
      this.filter = accordion.nestedFilter;
      this.filterHeaderButtons = accordion.filterHeaderButtons;
    }
  }

  detachFilter() {
    const { accordion } = this;
    if (accordion) {
      accordion.nestedFilter = this.filter;
      accordion.filterHeaderButtons = this.filterHeaderButtons;
    }
  }

  openDetailSheet(sanFilterFoldoutActivity: TrackingLabels["san_FilterFoldoutActivity"] = "open") {
    const { filterId, filterTitle } = this;
    const tracking: Partial<TrackingLabels> = {
      san_FilterFoldoutType: filterId,
      san_FilterFoldoutActivity: sanFilterFoldoutActivity,
      ...readLegacyLabels(this.elem),
    };

    this.filter?.updateChildrenFeatureStatus(true);
    switch (filterId) {
      case "localNavigation":
        submitEventTracking(tracking, createEventMergeId());
        eventQBus.emit("ftfind.filterSheet.openNavigation");
        break;
      default:
        submitActionTrackingFrom(this.elem, "open", tracking, listFeatures(["Filter-Overview"]));
        eventQBus.emit("ftfind.filterSheet.openDetail", { filterId, filterTitle });
    }
  }

  toggleInlineMode(force = !this.onlyPopularValuesAndTitleMode) {
    this.onlyPopularValuesAndTitleMode = force;
  }

  invalidate() {
    this.elem.classList.add(FILTER_TITLE_INVALID_MODIFIER);
  }

  validate() {
    this.elem.classList.remove(FILTER_TITLE_INVALID_MODIFIER);
  }

  remove() {
    const parentElement = this.elem?.parentElement;
    if (parentElement?.matches("oc-row-v2")) {
      parentElement.remove();
    }
    this.elem?.remove();
  }
}
